// A still picture and its camera, standing in for the engine's picture inside the mock viewport.
// scripts/parity.ts uses it to put the design's Cycles plates (design/system/screens/plates) behind
// the app, framed the way the design's screens frame them, so the app's chrome and HUD can be
// compared with the design's captures pixel for pixel. Ported from design/system/screens/screens.js
// (loadPlate, paintViewport, tissueMask, paintSurface): the same canvas operations in the same
// order, so the same Chrome paints the same pixels.
//
// The framing is the viewport API's: the plate's principal point goes to the centre of the clear
// area (the insets, as a lens shift) and the plate is scaled by `zoom` about it, which is a camera
// with the plate's pose and a focal length `zoom` times the plate's.

import type { Vec3 } from './api.ts';

/** design/tokens.json camera.fov_deg, vertical (the plates' render_pack.py: sensor_fit VERTICAL). */
const PLATE_FOV_DEG = 35;
/** Plates are rendered at 2× of the frame they're framed for. */
const PLATE_DPR = 2;

export interface StillAnchor {
  id: string;
  /** A point on the structure's visible surface, RAS mm. */
  at: Vec3;
  /** The visible area the `labels` event reports for it, px² (orders the candidates). */
  area: number;
  /** False: no visible pixels (anchor bit 0 clear). */
  visible?: boolean;
}

/** Regions painted on one hemisphere's lateral surface, as the engine's overlays would. */
export interface StillPaint {
  side: 1 | -1;
  opacity: number;
  blend?: GlobalCompositeOperation;
  /** Rings of [y, z] mm; `color` is a CSS custom property, `blur` in px. */
  regions: { ring: [number, number][]; color: string; blur: number }[];
}

export interface StillSpec {
  /** URL of the plate without its extension: `<plate>.jpg` and `<plate>.png.json`. */
  plate: string;
  zoom: number;
  /** Frame for this clear area instead of the app's insets, so the app's chrome can be compared
   *  with a screen whose framing it doesn't share (parity reports the inset difference itself). */
  insets?: { top: number; right: number; bottom: number; left: number };
  anchors: StillAnchor[];
  paint?: StillPaint;
}

interface PlateMeta {
  size: [number, number];
  camera: { target_m: Vec3; distance_m: number };
  orientation: { camera_direction_ras: Vec3; camera_right_ras: Vec3; camera_up_ras: Vec3 };
}

export interface Plate {
  img: HTMLImageElement;
  /** Frame size in CSS px (the plate's pixels over its DPR). */
  w: number;
  h: number;
  /** Focal length in CSS px at zoom 1. */
  f: number;
  targetMm: Vec3;
  distanceMm: number;
  /** Camera basis in RAS: d from the target towards the camera, r right, u up. */
  d: Vec3;
  r: Vec3;
  u: Vec3;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, err) => {
    const img = new Image();
    img.onload = () => ok(img);
    img.onerror = () => err(new Error(`image ${src} did not load`));
    img.src = src;
  });
}

export async function loadPlate(url: string): Promise<Plate> {
  const [img, meta] = await Promise.all([
    loadImage(`${url}.jpg`),
    fetch(`${url}.png.json`).then((r) => {
      if (!r.ok) throw new Error(`${url}.png.json: HTTP ${r.status}`);
      return r.json() as Promise<PlateMeta>;
    }),
  ]);
  const o = meta.orientation;
  const w = meta.size[0] / PLATE_DPR;
  const h = meta.size[1] / PLATE_DPR;
  return {
    img,
    w,
    h,
    f: h / 2 / Math.tan((PLATE_FOV_DEG * Math.PI) / 360),
    targetMm: meta.camera.target_m.map((v) => v * 1000) as Vec3,
    distanceMm: meta.camera.distance_m * 1000,
    d: o.camera_direction_ras,
    r: o.camera_right_ras,
    u: o.camera_up_ras,
  };
}

const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export interface Framing {
  /** Zoom: the plate's scale on screen. */
  s: number;
  /** The clear area's centre, CSS px. */
  cx: number;
  cy: number;
}

export function eyeOf(pl: Plate): Vec3 {
  return [0, 1, 2].map((i) => pl.targetMm[i]! + pl.d[i]! * pl.distanceMm) as Vec3;
}

/**
 * The camera as the viewport API reports it: a column-major view-projection with the lens shift
 * in its z column, and the vertical field of view that puts the plate's focal length × zoom on a
 * canvas `height` px high.
 */
export function stillCamera(pl: Plate, fr: Framing, width: number, height: number): { viewProj: Float32Array; fovYDeg: number; eye: Vec3 } {
  const eye = eyeOf(pl);
  const F = pl.f * fr.s;
  const fy = (2 * F) / height; // 1 / tan(fovY / 2)
  const fovYDeg = (2 * Math.atan(1 / fy) * 180) / Math.PI;
  // View: rows r, u, d; the camera looks along −d.
  const view = [pl.r, pl.u, pl.d];
  const t = view.map((axis) => -dot(axis, eye));
  // Lens shift in NDC: the principal point at (cx, cy) instead of the canvas centre.
  const sx = (2 * fr.cx) / width - 1;
  const sy = 1 - (2 * fr.cy) / height;
  const near = 1;
  const far = 5000;
  const m = new Float32Array(16);
  // clip.x = (F / (w/2)) · x_v − sx · z_v ; clip.y = fy · y_v − sy · z_v ; clip.w = −z_v
  const ax = (2 * F) / width;
  for (let c = 0; c < 3; c++) {
    m[c * 4 + 0] = ax * view[0]![c]! - sx * view[2]![c]!;
    m[c * 4 + 1] = fy * view[1]![c]! - sy * view[2]![c]!;
    m[c * 4 + 2] = (far / (near - far)) * view[2]![c]!;
    m[c * 4 + 3] = -view[2]![c]!;
  }
  m[12] = ax * t[0]! - sx * t[2]!;
  m[13] = fy * t[1]! - sy * t[2]!;
  m[14] = (far / (near - far)) * t[2]! + (near * far) / (near - far);
  m[15] = -t[2]!;
  return { viewProj: m, fovYDeg, eye };
}

/** RAS mm → CSS px through the plate's camera at the framing (screens.js project). */
export function projectStill(pl: Plate, fr: Framing, p: Vec3): [number, number, number] {
  const c = eyeOf(pl);
  const rel: Vec3 = [p[0] - c[0], p[1] - c[1], p[2] - c[2]];
  const zc = -dot(rel, pl.d);
  const x = pl.w / 2 + (pl.f * dot(rel, pl.r)) / zc;
  const y = pl.h / 2 - (pl.f * dot(rel, pl.u)) / zc;
  return [fr.cx + (x - pl.w / 2) * fr.s, fr.cy + (y - pl.h / 2) * fr.s, zc];
}

/** Paint the plate at the framing on its own backdrop gradient, edges feathered (screens.js paintViewport). */
export function paintPlate(ctx: CanvasRenderingContext2D, pl: Plate, fr: Framing, W: number, H: number, DPR: number) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const nat = document.createElement('canvas');
  nat.width = pl.img.naturalWidth;
  nat.height = pl.img.naturalHeight;
  const nctx = nat.getContext('2d', { willReadFrequently: true })!;
  nctx.drawImage(pl.img, 0, 0);
  // The render's backdrop is a vertical gradient; read it from the plate's outer columns.
  const cols = nctx.getImageData(0, 0, 12, nat.height).data;
  const prof: [number, number, number][] = [];
  for (let y = 0; y < nat.height; y++) {
    let r = 0;
    let g = 0;
    let b = 0;
    for (let x = 2; x < 12; x++) {
      const i = (y * 12 + x) * 4;
      r += cols[i]!;
      g += cols[i + 1]!;
      b += cols[i + 2]!;
    }
    prof.push([r / 10, g / 10, b / 10]);
  }
  const ox = fr.cx - (fr.s * pl.w) / 2;
  const oy = fr.cy - (fr.s * pl.h) / 2;
  for (let y = 0; y < H * DPR; y++) {
    const py = Math.round(((y / DPR - oy) / fr.s) * DPR);
    const c = prof[Math.max(0, Math.min(prof.length - 1, py))]!;
    ctx.fillStyle = `rgb(${c[0]},${c[1]},${c[2]})`;
    ctx.fillRect(0, y, W * DPR, 1);
  }
  const sw = Math.round(fr.s * pl.w * DPR);
  const sh = Math.round(fr.s * pl.h * DPR);
  const tmp = document.createElement('canvas');
  tmp.width = sw;
  tmp.height = sh;
  const t = tmp.getContext('2d')!;
  t.imageSmoothingQuality = 'high';
  t.drawImage(pl.img, 0, 0, sw, sh);
  t.globalCompositeOperation = 'destination-in';
  const feather = (horizontal: boolean, frac: number) => {
    const g = horizontal ? t.createLinearGradient(0, 0, sw, 0) : t.createLinearGradient(0, 0, 0, sh);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(frac, 'rgba(0,0,0,1)');
    g.addColorStop(1 - frac, 'rgba(0,0,0,1)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    t.fillStyle = g;
    t.fillRect(0, 0, sw, sh);
  };
  if (fr.s < 1) {
    feather(false, 0.1); // the ghosted scalp runs off the plate's top and bottom
    feather(true, 0.05);
  }
  ctx.drawImage(tmp, Math.round(ox * DPR), Math.round(oy * DPR));
}

/** A soft mask of the brain's silhouette at the framing, for paint that must stay on tissue. */
function tissueMask(pl: Plate, fr: Framing, DPR: number) {
  const sw = Math.round(fr.s * pl.w * DPR);
  const sh = Math.round(fr.s * pl.h * DPR);
  const c = document.createElement('canvas');
  c.width = sw;
  c.height = sh;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(pl.img, 0, 0, sw, sh);
  const im = ctx.getImageData(0, 0, sw, sh);
  const d = im.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i]! + d[i + 1]! + d[i + 2]!) / 3;
    const a = Math.max(0, Math.min(1, (lum - 92) / 26));
    d[i] = d[i + 1] = d[i + 2] = 0;
    d[i + 3] = Math.round(a * 255);
  }
  ctx.putImageData(im, 0, 0);
  const out = document.createElement('canvas');
  out.width = sw;
  out.height = sh;
  const o = out.getContext('2d')!;
  o.filter = `blur(${DPR}px)`;
  o.drawImage(c, 0, 0);
  return { canvas: out, ox: fr.cx - (fr.s * pl.w) / 2, oy: fr.cy - (fr.s * pl.h) / 2 };
}

/** A closed smooth path through points (Catmull-Rom to Bézier). */
function smoothPath(ctx: CanvasRenderingContext2D, pts: number[][]) {
  const n = pts.length;
  ctx.beginPath();
  ctx.moveTo(pts[0]![0]!, pts[0]![1]!);
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n]!;
    const p1 = pts[i]!;
    const p2 = pts[(i + 1) % n]!;
    const p3 = pts[(i + 2) % n]!;
    ctx.bezierCurveTo(p1[0]! + (p2[0]! - p0[0]!) / 6, p1[1]! + (p2[1]! - p0[1]!) / 6, p2[0]! - (p3[0]! - p1[0]!) / 6, p2[1]! - (p3[1]! - p1[1]!) / 6, p2[0]!, p2[1]!);
  }
  ctx.closePath();
}

/** Lateral-surface points for a hemisphere: x from an ellipsoid fitted to the MNI brain. */
function onSurface(side: number, y: number, z: number): Vec3 {
  const q = 1 - ((y + 17) / 92) ** 2 - ((z - 10) / 74) ** 2;
  return [side * 68 * Math.sqrt(Math.max(0.04, q)), y, z];
}

/** Regions on the anatomy, blurred, masked to tissue and laid over the picture (screens.js paintSurface). */
export function paintSurface(ctx: CanvasRenderingContext2D, pl: Plate, fr: Framing, paint: StillPaint, W: number, H: number, DPR: number) {
  const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const layer = document.createElement('canvas');
  layer.width = W * DPR;
  layer.height = H * DPR;
  const l = layer.getContext('2d')!;
  for (const region of paint.regions) {
    const pts = region.ring.map(([y, z]) => projectStill(pl, fr, onSurface(paint.side, y, z)).slice(0, 2).map((v) => v * DPR));
    l.save();
    l.filter = `blur(${region.blur * DPR}px)`;
    l.fillStyle = css(region.color);
    smoothPath(l, pts);
    l.fill();
    l.restore();
  }
  const mask = tissueMask(pl, fr, DPR);
  l.globalCompositeOperation = 'destination-in';
  l.drawImage(mask.canvas, Math.round(mask.ox * DPR), Math.round(mask.oy * DPR));
  ctx.save();
  ctx.globalAlpha = paint.opacity;
  ctx.globalCompositeOperation = paint.blend ?? 'source-over';
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
}
